// src/services/notifications/NotificationService.js
import { reactive } from 'vue';
import { WhatsAppStrategy } from '@/services/notifications/strategies/WhatsAppStrategy';
import { EmailStrategy } from '@/services/notifications/strategies/EmailStrategy';

class NotificationService {
  constructor() {
    console.log("NotificationService: Iniciando construtor");
    
    this.strategies = new Map();
    this.state = reactive({
      pending: false,
      error: null,
    });

    // Inicializar estratégias com tratamento de erros
    try {
      console.log("NotificationService: Criando estratégia de WhatsApp");
      const whatsAppStrategy = new WhatsAppStrategy();
      this.strategies.set('whatsapp', whatsAppStrategy);
      console.log("NotificationService: Estratégia de WhatsApp configurada com sucesso");
    } catch (error) {
      console.error("NotificationService: Erro ao criar estratégia de WhatsApp:", error);
      
      // Fallback para não quebrar a aplicação
      console.warn("NotificationService: Usando implementação de fallback para WhatsApp");
      this.strategies.set('whatsapp', {
        sendWhatsappTemplate: async (template, params, to, profileName) => {
          console.warn("NotificationService: Usando fallback para WhatsApp template", template, profileName);
          return false;
        },
        send: async (payload, profileName) => {
          console.warn("NotificationService: Usando fallback para WhatsApp send", profileName);
          return false;
        }
      });
    }
    
    try {
      this.strategies.set('email', new EmailStrategy());
    } catch (error) {
      console.error("NotificationService: Erro ao criar estratégia de Email:", error);
    }
    
    console.log("NotificationService: Inicialização concluída");
  }

  async notify(channels, payload, profileName = null) {
    this.state.pending = true;
    this.state.error = null;
    const results = {};

    try {
      for (const channel of channels) {
        const strategy = this.strategies.get(channel);
        if (!strategy) {
          throw new Error(`Canal de notificação não suportado: ${channel}`);
        }
        
        // Se for whatsapp, passar o profileName
        if (channel === 'whatsapp') {
          results[channel] = await strategy.send(payload, profileName);
        } else {
          results[channel] = await strategy.send(payload);
        }
      }
    } catch (error) {
      this.state.error = error.message || 'Erro desconhecido';
      console.error('NotificationService: Erro no serviço de notificação:', error);
    } finally {
      this.state.pending = false;
    }

    return results;
  }

  async sendTo(channel, payload, profileName = null) {
    // Para whatsapp, permitir especificar o perfil
    if (channel === 'whatsapp') {
      const result = await this.notify([channel], payload, profileName);
      return result[channel] || false;
    } else {
      const result = await this.notify([channel], payload);
      return result[channel] || false;
    }
  }

  async sendEmailTemplate(template, params, to) {
    const emailStrategy = this.strategies.get('email');
    if (!emailStrategy) {
      console.error('NotificationService: Estratégia de email não configurada');
      return false;
    }
    
    try {
      return await emailStrategy.sendEmailTemplate(template, params, to);
    } catch (error) {
      console.error('NotificationService: Erro ao enviar template de email:', error);
      return false;
    }
  }

  async sendWhatsappTemplate(template, params, to, profileName = null) {
    console.log("NotificationService: sendWhatsappTemplate chamado com template:", template, "perfil:", profileName);
    
    const whatsappStrategy = this.strategies.get('whatsapp');
    if (!whatsappStrategy) {
      console.error('NotificationService: Estratégia de whatsapp não encontrada');
      return false;
    }
    
    if (!whatsappStrategy.sendWhatsappTemplate) {
      console.error('NotificationService: Método sendWhatsappTemplate não encontrado na estratégia');
      return false;
    }
    
    try {
      return await whatsappStrategy.sendWhatsappTemplate(template, params, to, profileName);
    } catch (error) {
      console.error("NotificationService: Erro ao enviar template de WhatsApp:", error);
      return false;
    }
  }
}

// Exporta uma única instância do serviço
export const notificationService = new NotificationService();